/**
 * Listing state → label and tone.
 *
 * One map, imported by every screen that renders a listing's state. It was
 * copy-pasted into All Bikes, Edit Bike and Analytics with drifting labels, so
 * adding a state meant three edits and forgetting one silently rendered a raw
 * enum at the dealer.
 *
 * Kept out of `components/Page.tsx` because that file must only export
 * components — a const export there defeats React Fast Refresh, and the lint
 * rule that catches it is on.
 */

/** The five tones the palette defines. Nothing else is a valid pill. */
export type StatusTone = "live" | "review" | "paused" | "dead" | "failed";

export interface StatusMeta {
  label: string;
  tone: StatusTone;
}

export const LISTING_STATUS: Record<string, StatusMeta> = {
  DRAFT: { label: "Draft", tone: "dead" },
  PENDING_REVIEW: { label: "In review", tone: "review" },
  PUBLISHED: { label: "Live", tone: "live" },
  PAUSED: { label: "Paused", tone: "paused" },
  SOLD: { label: "Sold", tone: "dead" },
  EXPIRED: { label: "Expired", tone: "dead" },
  REJECTED: { label: "Rejected", tone: "failed" },
  REMOVED: { label: "Removed", tone: "failed" },
};

/** An unknown state still renders as words, never as a raw enum. */
export function listingStatusMeta(status: string): StatusMeta {
  return (
    LISTING_STATUS[status] ?? {
      label: status.replace(/_/g, " ").toLowerCase(),
      tone: "dead",
    }
  );
}

export const STATUS_TONE_CLASS: Record<StatusTone, string> = {
  live: "bg-status-live-wash text-status-live",
  review: "bg-status-review-wash text-status-review",
  paused: "bg-status-paused-wash text-status-paused",
  dead: "bg-status-dead-wash text-status-dead",
  failed: "bg-status-failed-wash text-status-failed",
};

export const STATUS_DOT_CLASS: Record<StatusTone, string> = {
  live: "bg-status-live",
  review: "bg-status-review",
  paused: "bg-status-paused",
  dead: "bg-status-dead",
  failed: "bg-status-failed",
};
